import { TypedWorker } from '../../workers/TypedWorker.js';
import WorkerWrapper from '../workers/lowlight.worker2.js';

class LowlightWorkerManager {
  /**
  * Private constructor to enforce singleton pattern.
  */
  constructor() {
    this.worker = null;
    this.terminationTimeoutId = null;
    this.tryingToTerminateSince = null;
  }
  static {
    this.INACTIVITY_TIMEOUT_MS = 30 * 1e3;
  }
  static {
    this.MAX_PENDING_WAIT_MS = 2 * 60 * 1e3;
  }
  static {
    this.PENDING_CHECK_INTERVAL_MS = 5 * 1e3;
  }
  /**
  * Gets the singleton instance of the LowlightWorkerManager.
  * @returns {LowlightWorkerManager} The singleton instance.
  */
  static getInstance() {
    if (!LowlightWorkerManager.instance) {
      LowlightWorkerManager.instance = new LowlightWorkerManager();
    }
    return LowlightWorkerManager.instance;
  }
  /**
  * Retrieves the shared TypedWorker instance.
  * If the worker doesn't exist, it's created.
  * Each call resets any pending termination process and schedules a new inactivity timeout.
  * @returns {TypedWorker<LowlightWorkerMessage>} The shared TypedWorker instance.
  */
  getWorker() {
    this.clearTerminationTimeout();
    this.tryingToTerminateSince = null;
    if (!this.worker) {
      const webWorker = new WorkerWrapper();
      this.worker = new TypedWorker(webWorker, "GlobalLowlightWorker");
    }
    this.scheduleTermination(LowlightWorkerManager.INACTIVITY_TIMEOUT_MS);
    return this.worker;
  }
  /**
  * Schedules the termination of the worker after a specified delay.
  * Handles logic for pending requests and maximum wait times.
  * @param {number} delay - The delay in milliseconds before checking for termination.
  */
  scheduleTermination(delay) {
    this.clearTerminationTimeout();
    this.terminationTimeoutId = setTimeout(() => {
      if (!this.worker) {
        this.tryingToTerminateSince = null;
        return;
      }
      if (this.worker.hasPendingRequests) {
        if (this.tryingToTerminateSince === null) {
          this.tryingToTerminateSince = Date.now();
        }
        const timeSpentWaiting = Date.now() - this.tryingToTerminateSince;
        if (timeSpentWaiting >= LowlightWorkerManager.MAX_PENDING_WAIT_MS) {
          this.terminateWorker();
        } else {
          this.scheduleTermination(LowlightWorkerManager.PENDING_CHECK_INTERVAL_MS);
        }
      } else {
        this.terminateWorker();
      }
    }, delay);
  }
  /**
  * Terminates the worker and resets related state.
  */
  terminateWorker() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.tryingToTerminateSince = null;
    this.clearTerminationTimeout();
  }
  /**
  * Clears any scheduled termination timeout.
  */
  clearTerminationTimeout() {
    if (this.terminationTimeoutId) {
      clearTimeout(this.terminationTimeoutId);
      this.terminationTimeoutId = null;
    }
  }
}

export { LowlightWorkerManager };
//# sourceMappingURL=LowlightWorkerManager.js.map
